/**
 * CablePathRenderer
 *
 * Renders a single cable path as a polyline on the floor plan.
 * Paths can be conduit or cable-only, with different visual styling.
 */

import React, { useMemo } from 'react';
import type { CablePath } from '@/types/cablePaths';
import type { Point } from '@/types/common';
import { formatMeasurement } from '@/utils/scaleCalculations';
import type { ScaleData } from '@/types/floorPlanViewer';

interface CablePathRendererProps {
  path: CablePath;
  /** Full points including junction coordinates */
  fullPoints: Point[];
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  scaleData: ScaleData | null;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
  showLengthLabel?: boolean;
}

// Path styling constants - fixed in floor plan coordinates (scale with zoom)
const PATH_STROKE_WIDTH = 2;
const HIT_AREA_WIDTH = 20;

export function CablePathRenderer({
  path,
  fullPoints,
  isSelected,
  isHovered,
  scale,
  scaleData,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  isInteractive,
  showLengthLabel = true,
}: CablePathRendererProps) {
  // Build SVG path string from points
  const pathString = useMemo(() => {
    if (fullPoints.length < 2) return '';
    const [first, ...rest] = fullPoints;
    return `M ${first.x} ${first.y} ${rest.map((p) => `L ${p.x} ${p.y}`).join(' ')}`;
  }, [fullPoints]);

  // Calculate label position (midpoint of path)
  const labelPosition = useMemo(() => {
    if (fullPoints.length < 2) return null;
    const midIndex = Math.floor(fullPoints.length / 2);
    if (fullPoints.length % 2 === 0) {
      // Even number of points - average of two middle points
      const p1 = fullPoints[midIndex - 1];
      const p2 = fullPoints[midIndex];
      return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    }
    return fullPoints[midIndex];
  }, [fullPoints]);

  // Calculate dimensions
  // Path stroke is fixed in floor plan coordinates (scales with zoom)
  const strokeWidth = PATH_STROKE_WIDTH;
  // Hit area scales with zoom for easier selection when zoomed out
  const hitAreaWidth = HIT_AREA_WIDTH / scale;
  // Labels scale with zoom to remain readable
  const fontSize = 11 / scale;
  const labelPadding = 4 / scale;

  // Get colors based on state and path type
  const getStrokeColor = () => {
    if (isSelected) return '#3b82f6'; // Blue when selected
    if (isHovered) return '#60a5fa'; // Lighter blue when hovered
    // Different colors for conduit vs cable-only
    return path.path_type === 'conduit' ? '#4b5563' : '#6b7280';
  };

  const getDashArray = () => {
    // Conduit is solid, cable-only is dashed
    if (path.path_type === 'conduit') return undefined;
    // Dash array is fixed in floor plan coordinates
    return `${6} ${3}`;
  };

  // Format length for display
  const lengthLabel = useMemo(() => {
    if (!showLengthLabel || !path.length_real || !scaleData) return null;
    return formatMeasurement(path.length_real * scaleData.pixelsPerUnit, scaleData);
  }, [showLengthLabel, path.length_real, scaleData]);

  if (fullPoints.length < 2) return null;

  return (
    <g data-path-id={path.id}>
      {/* Invisible wider hit area for easier selection */}
      <path
        d={pathString}
        fill="none"
        stroke="transparent"
        strokeWidth={hitAreaWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={{
          cursor: isInteractive ? 'pointer' : 'default',
          pointerEvents: isInteractive ? 'stroke' : 'none',
        }}
      />

      {/* Main path line */}
      <path
        d={pathString}
        fill="none"
        stroke={getStrokeColor()}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={getDashArray()}
        style={{ pointerEvents: 'none' }}
      />

      {/* Vertex markers when selected - only intermediate points, not endpoints */}
      {/* Endpoints are shown by junction boxes, so we skip first and last */}

      {/* Length label */}
      {lengthLabel && labelPosition && (isSelected || isHovered) && (
        <g style={{ pointerEvents: 'none' }}>
          {/* Label background */}
          <rect
            x={labelPosition.x - 30 / scale}
            y={labelPosition.y - fontSize - labelPadding}
            width={60 / scale}
            height={fontSize + labelPadding * 2}
            rx={3 / scale}
            fill="white"
            fillOpacity={0.9}
            stroke={isSelected ? '#3b82f6' : '#9ca3af'}
            strokeWidth={1 / scale}
          />
          {/* Label text */}
          <text
            x={labelPosition.x}
            y={labelPosition.y}
            textAnchor="middle"
            fill={isSelected ? '#3b82f6' : '#374151'}
            fontSize={fontSize}
            fontWeight={500}
            fontFamily="system-ui, sans-serif"
          >
            {lengthLabel}
          </text>
        </g>
      )}

      {/* Path name label (shown when selected) */}
      {path.name && isSelected && labelPosition && (
        <text
          x={labelPosition.x}
          y={labelPosition.y + fontSize * 2}
          textAnchor="middle"
          fill="#6b7280"
          fontSize={fontSize * 0.9}
          fontFamily="system-ui, sans-serif"
          style={{ pointerEvents: 'none' }}
        >
          {path.name}
        </text>
      )}

      {/* Route indicator icon (C=ceiling, F=floor, W=wall) */}
      {isSelected && fullPoints.length > 0 && (
        <g style={{ pointerEvents: 'none' }}>
          <circle
            cx={fullPoints[0].x}
            cy={fullPoints[0].y - 12 / scale}
            r={8 / scale}
            fill="white"
            stroke={getStrokeColor()}
            strokeWidth={1 / scale}
          />
          <text
            x={fullPoints[0].x}
            y={fullPoints[0].y - 8 / scale}
            textAnchor="middle"
            fill={getStrokeColor()}
            fontSize={8 / scale}
            fontWeight={600}
            fontFamily="system-ui, sans-serif"
          >
            {path.route === 'ceiling' ? 'C' : path.route === 'floor' ? 'F' : 'W'}
          </text>
        </g>
      )}
    </g>
  );
}
